import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { Store } from "@ngxs/store";
import {
    ClearProjects,
    LoadProjects,
    SavePagination,
} from "src/store/projects/actions/set-projects.action";
import { SaveCurrentProject } from "src/store/projects/actions/set-current-project.action";
import { SaveCreatedProjectId } from "src/store/projects/actions/set-project-id.action";
import { ToastService } from "src/app/services/toast.service";
import { GeneralSuccessResp } from "src/app/services/response-models/companies.response.model";
import {
    CreateProject,
    CurrentProject,
    CustomProjectStatuses,
    Pagination,
    ProjectData,
    ProjectOverview,
    Projects,
} from "src/app/services/response-models/projects.response.model";
import { ProductConsumptionResponse } from "src/app/services/response-models/product.response.model";
import { UploadedFileWithTypePagination } from "src/app/services/models/misc.model";
import { sentryCaptureException } from "src/app/utility/errors";
import { lastValueFrom } from "rxjs";
import { UtilityService } from "src/app/services/utility.service";
import { IncludeType } from "src/app/shared/enums/include-type.enum";

@Injectable({
    providedIn: "root",
})
export class ProjectsService {
    private readonly projectsUrl = `${environment.apiBaseUrl}projects`;
    private readonly projectStatusesUrl = `${environment.apiBaseUrl}project_statuses`;

    constructor(
        private http: HttpClient,
        private store: Store,
        private toastService: ToastService
    ) {}

    public async getProjects(
        page = 1,
        query = "",
        includes = "",
        sortBy = "",
        direction = "",
        project_status_ids: Array<string> = [],
        limit = "20",
        contactId = "",
        activities: Array<string> = [],
        is_offer = "",
        employeeIds = "",
        withChildProjects = true,
        useStore = true,
        projectLabels: Array<string> = [],
        customerLabels: Array<string> = []
    ): Promise<Projects> {
        const headers = {
            params: {
                page: page.toString(),
                q: query,
                include: includes,
                sort: sortBy,
                direction,
                limit,
                contact_id: contactId,
                is_offer,
                employee_ids: employeeIds,
                withChildProjects,
            },
        };

        if (!withChildProjects) {
            delete headers.params.withChildProjects;
        }

        if (!employeeIds) {
            delete headers.params.employee_ids;
        }

        if (is_offer?.length === 0) {
            delete headers.params.is_offer;
        }

        if (projectLabels?.length) {
            for (let i = 0; i < projectLabels.length; i++) {
                headers.params[`label_ids[${i}]`] = projectLabels[i];
            }
        }

        if (customerLabels?.length) {
            for (let i = 0; i < customerLabels.length; i++) {
                headers.params[`customer_label_ids[${i}]`] = customerLabels[i];
            }
        }

        // Sort by filter first if any
        if (project_status_ids.length) {
            for (let i = 0; i < project_status_ids.length; i++) {
                headers.params[`project_status_ids[${i}]`] = project_status_ids[i];
            }
        }

        if (activities.length) {
            for (let i = 0; i < activities.length; i++) {
                headers.params[`activity_ids[${i}]`] = activities[i];
            }
        }

        if (limit !== "" && limit !== null) {
            headers.params.limit = limit;
        }

        const { data, pagination } = await lastValueFrom(
            this.http.get<Projects>(this.projectsUrl, headers)
        ).catch((err) => {
            sentryCaptureException(err);
            this.toastService.showToastMessage("TOAST.BAD_CONNECTION");
            return { data: [], pagination: null };
        });

        if (page === 1 && useStore) {
            this.store.dispatch(new ClearProjects());
        }

        if (useStore) {
            this.store.dispatch(new SavePagination(pagination as Pagination));
            this.store.dispatch(new LoadProjects(data));
        }

        return { data, pagination } as Projects;
    }

    public async getLastUsedProjects(
        page = 1,
        query = "",
        sortBy = "",
        direction = "",
        filters: Array<string> = [],
        employeeIds = "",
        projectLabels: Array<string> = [],
        customerLabels: Array<string> = []
    ): Promise<Projects> {
        const url = `${this.projectsUrl}/recently-used`;

        let includes = UtilityService.getIncludes([
            IncludeType.Cities,
            IncludeType.Labels,
            IncludeType.Contacts,
            IncludeType.ContactPersons,
            IncludeType.ProjectStatuses,
        ]);

        // Label filtering lists matching sub-projects as flat rows, so skip
        // nested child projects there; otherwise nest them under their parent.
        if (projectLabels === null && customerLabels === null) {
            includes +=
                "," +
                UtilityService.getIncludes([
                    IncludeType.ChildProjects,
                    [IncludeType.ChildProjects, IncludeType.Contacts],
                    [IncludeType.ChildProjects, IncludeType.Labels],
                ]);
        }

        const headers = {
            params: {
                page: page.toString(),
                q: query,
                include: includes,
                sort: sortBy,
                direction,
                employee_ids: employeeIds,
                ...(projectLabels || customerLabels ? { withChildProjects: true } : {}),
            },
        };

        if (!employeeIds) {
            delete headers.params.employee_ids;
        }

        if (projectLabels?.length) {
            for (let i = 0; i < projectLabels.length; i++) {
                headers.params[`label_ids[${i}]`] = projectLabels[i];
            }
        }

        if (customerLabels?.length) {
            for (let i = 0; i < customerLabels.length; i++) {
                headers.params[`customer_label_ids[${i}]`] = customerLabels[i];
            }
        }

        if (filters.length) {
            for (let i = 0; i < filters.length; i++) {
                headers.params[`project_status_ids[${i}]`] = filters[i];
            }
        }

        const { data, pagination } = await lastValueFrom(
            this.http.get<Projects>(url, headers)
        ).catch(() => {
            this.toastService.showToastMessage("TOAST.BAD_CONNECTION");
            return { data: [], pagination: null };
        });

        if (page === 1) {
            this.store.dispatch(new ClearProjects());
        }

        this.store.dispatch(new SavePagination(pagination));
        this.store.dispatch(new LoadProjects(data));

        return { data, pagination } as Projects;
    }

    public async getProjectById(
        id: string,
        include = "labels,contacts.labels,events.event_tasks,cities,contact_persons,project_custom_field_values",
        skipSavingProject = false
    ): Promise<ProjectData> {
        const headers = {
            params: {
                include,
            },
        };
        const { data } = await lastValueFrom(
            this.http.get<CurrentProject>(`${this.projectsUrl}/${id}`, headers)
        );

        if (!skipSavingProject) {
            this.store.dispatch(new SaveCurrentProject(data));
        }

        return data;
    }

    public async createProject(body): Promise<ProjectData> {
        const { data } = await lastValueFrom(this.http.post<CreateProject>(this.projectsUrl, body));
        await this.getProjectById(data.id);
        this.store.dispatch(new SaveCreatedProjectId(data.id));
        return data;
    }

    public async editProject(projectId: string, body): Promise<GeneralSuccessResp> {
        return await lastValueFrom(
            this.http.put<GeneralSuccessResp>(`${this.projectsUrl}/${projectId}`, body)
        );
    }

    public getCustomProjectStatuses = async (statuses: string): Promise<CustomProjectStatuses> => {
        const headers = {
            params: {
                limit: "100",
                project_status_type_identifier: statuses,
            },
        };

        return await lastValueFrom(
            this.http.get<CustomProjectStatuses>(this.projectStatusesUrl, headers)
        );
    };

    /**
     * Check if user is able to submit a form for this project.
     * Project must be with status type identifier - "open"
     * @param projectId
     * @returns bool
     */
    public isProjectOpen = async (projectId: string): Promise<boolean> => {
        const url = `${this.projectsUrl}/${projectId}`;
        const headers = {
            params: {
                include: "project_statuses.project_status_types",
            },
        };

        const { data } = await lastValueFrom(this.http.get<CurrentProject>(url, headers));

        return data.project_status.project_status_type.identifier === "open";
    };

    /**
     * Check project status type
     *
     * @param projectId
     * @returns string
     */
    public getProjectStatus = async (projectId: string): Promise<string> => {
        const url = `${this.projectsUrl}/${projectId}`;
        const headers = {
            params: {
                include: "project_statuses.project_status_types",
            },
        };

        const { data } = await this.http.get<CurrentProject>(url, headers).toPromise();

        return data.project_status.project_status_type.identifier;
    };

    public getProjectOverview(
        id: string,
        date_from?: string,
        date_to?: string,
        activities?: string,
        includeChildData = false
    ): Promise<ProjectOverview> {
        const url = `${environment.apiBaseUrl}financial_statistics/overview`;

        const headers = {
            params: {
                project_id: id,
                include_child_data: includeChildData,
            } as any,
        };

        if (date_from && date_to) {
            headers.params.date_from = date_from;
            headers.params.date_to = date_to;
        }

        if (activities) {
            headers.params.activities = activities;
        }

        return lastValueFrom(this.http.get<ProjectOverview>(url, headers));
    }

    public getProductConsumption(
        projectId,
        page = 1,
        dateFrom: string,
        dateTo: string,
        includeChildData = false,
        limit = 10
    ): Promise<ProductConsumptionResponse> {
        const url = `${this.projectsUrl}/${projectId}/product_consumption`;
        const headers = {
            params: {
                project_id: projectId,
                page: page.toString(),
                date_from: dateFrom,
                date_to: dateTo,
                include_child_data: includeChildData,
                limit,
            },
        };

        return lastValueFrom(this.http.get<ProductConsumptionResponse>(url, headers));
    }

    public async getAllImagesForProject(
        projectId: string,
        includeChildData = false,
        page = 1
    ): Promise<UploadedFileWithTypePagination> {
        const headers = {
            params: {
                include_child_data: includeChildData,
                page: page,
            },
        };

        return lastValueFrom(
            this.http.get<UploadedFileWithTypePagination>(
                `${this.projectsUrl}/${projectId}/all_files/get_images`,
                headers
            )
        );
    }
}
